// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { spacing } from "@recidiviz/design-system";
import { descending } from "d3-array";
import { rem } from "polished";
import React from "react";
import styled from "styled-components";

import { formatWorkflowsDate } from "../../../utils";
import { WithCaseNotes } from "../../../WorkflowsStore";
import {
  CaseNoteDate,
  CaseNoteTitle,
  DetailsHeading,
  DetailsList,
  DetailsSection,
  DetailsSubheading,
  SecureDetailsContent,
} from "../styles";
import { OpportunityProfileProps } from "../types";

const CaseNotesList = styled(DetailsList)`
  padding-left: ${rem(spacing.xs)};
`;

export function CaseNotes({
  opportunity,
}: OpportunityProfileProps): React.ReactElement<any> | null {
  if (!opportunity.record) return null;

  const { caseNotes } = opportunity.record as Partial<WithCaseNotes>;
  if (!caseNotes) {
    return null;
  }

  const { caseNotesTitle } = opportunity;

  let caseNoteHeaders;

  if (opportunity.caseNoteHeaders?.length > 0) {
    caseNoteHeaders = [
      ...opportunity.caseNoteHeaders,
      ...Object.keys(caseNotes).filter(
        (header) => !opportunity.caseNoteHeaders.includes(header),
      ),
    ];
  } else {
    caseNoteHeaders = Object.keys(caseNotes);
  }

  if (caseNoteHeaders.length === 0) {
    return (
      <DetailsSection>
        <DetailsHeading>{caseNotesTitle}</DetailsHeading>
        <SecureDetailsContent>None</SecureDetailsContent>
      </DetailsSection>
    );
  }

  return (
    <DetailsSection className="DetailsSection">
      <DetailsHeading>{caseNotesTitle}</DetailsHeading>
      <SecureDetailsContent>
        <DetailsList>
          {caseNoteHeaders.map((caseNoteHeader) => {
            const notes = caseNotes[caseNoteHeader];

            if (notes && notes.length > 0) {
              return (
                <React.Fragment key={caseNoteHeader}>
                  <DetailsSubheading>{caseNoteHeader}</DetailsSubheading>
                  <CaseNotesList className="fs-exclude">
                    {notes
                      .sort((noteA, noteB) =>
                        descending(noteA.eventDate, noteB.eventDate),
                      )
                      .map((note, index) => {
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <SecureDetailsContent key={index}>
                            {note.noteTitle && (
                              <CaseNoteTitle>{note.noteTitle}: </CaseNoteTitle>
                            )}
                            {note.noteBody && note.noteBody}{" "}
                            {(note.eventDate ||
                              !opportunity.hideUnknownCaseNoteDates) && (
                              <CaseNoteDate>
                                {formatWorkflowsDate(note.eventDate)}
                              </CaseNoteDate>
                            )}
                          </SecureDetailsContent>
                        );
                      })}
                  </CaseNotesList>
                </React.Fragment>
              );
            } else {
              return (
                <React.Fragment key={caseNoteHeader}>
                  <DetailsSubheading>{caseNoteHeader}</DetailsSubheading>
                  <DetailsList className="fs-exclude">
                    <SecureDetailsContent>
                      No relevant contact notes found
                    </SecureDetailsContent>
                  </DetailsList>
                </React.Fragment>
              );
            }
          })}
        </DetailsList>
      </SecureDetailsContent>
    </DetailsSection>
  );
}
